// =====================================================
// RESUMEN EJECUTIVO - EVALUACIÓN CREDITICIA
// src/pages/Agro/Evaluacion/ResumenEjecutivo.js
// =====================================================
// Corregido para usar el Layout de EWAN correctamente
// =====================================================

import React, { useState, useEffect } from "react"
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Badge,
  Table,
  Button,
  Alert,
  Progress,
  Spinner,
  Collapse,
} from "reactstrap"
import { useParams, useHistory, Link } from "react-router-dom"

import Layout from "components/Layout"
import { getEvaluacion, aprobarEvaluacion, rechazarEvaluacion } from "api/agro"
import toastHandler from "toastHandler"

// Colores por score
const getScoreColor = categoria => {
  const colores = {
    A: "success",
    B: "info",
    C: "warning",
    D: "orange",
    E: "danger",
  }
  return colores[categoria] || "secondary"
}

// Recomendación por categoría
const getRecomendacion = categoria => {
  const recs = {
    A: { texto: "APROBAR", color: "success", icon: "mdi-check-circle" },
    B: {
      texto: "APROBAR CON SEGUIMIENTO",
      color: "info",
      icon: "mdi-check-circle-outline",
    },
    C: { texto: "REVISAR CONDICIONES", color: "warning", icon: "mdi-alert" },
    D: {
      texto: "REQUIERE GARANTÍAS",
      color: "orange",
      icon: "mdi-alert-outline",
    },
    E: { texto: "NO RECOMENDADO", color: "danger", icon: "mdi-close-circle" },
  }
  return recs[categoria] || recs.C
}

// Formatear dinero
const formatMoney = value => {
  if (!value && value !== 0) return "-"
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(value)
}

const ResumenEjecutivo = ({ permissionBy }) => {
  const { idEvaluacion } = useParams()
  const history = useHistory()
  const { setError, setSuccess } = toastHandler()

  const [loading, setLoading] = useState(true)
  const [evaluacion, setEvaluacion] = useState(null)
  const [mostrarAnalisis, setMostrarAnalisis] = useState(false)
  const [procesando, setProcesando] = useState(false)

  useEffect(() => {
    cargarEvaluacion()
  }, [idEvaluacion])

  const cargarEvaluacion = () => {
    setLoading(true)
    getEvaluacion(idEvaluacion)
      .then(data => setEvaluacion(data))
      .catch(err => setError(err))
      .finally(() => setLoading(false))
  }

  const handleAprobar = () => {
    if (!window.confirm("¿Confirma la APROBACIÓN de este crédito?")) return
    setProcesando(true)
    aprobarEvaluacion(idEvaluacion)
      .then(() => {
        setSuccess("Crédito aprobado exitosamente")
        history.push("/agro/evaluaciones")
      })
      .catch(err => setError(err))
      .finally(() => setProcesando(false))
  }

  const handleRechazar = () => {
    const motivo = window.prompt("Ingrese el motivo del rechazo:")
    if (!motivo) return
    setProcesando(true)
    rechazarEvaluacion(idEvaluacion, motivo)
      .then(() => {
        setSuccess("Crédito rechazado")
        history.push("/agro/evaluaciones")
      })
      .catch(err => setError(err))
      .finally(() => setProcesando(false))
  }

  // =====================================================
  // CONTENIDO PRINCIPAL
  // =====================================================
  const MainContent = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
          <p className="mt-3">Cargando evaluación...</p>
        </div>
      )
    }

    if (!evaluacion) {
      return <Alert color="danger">No se encontró la evaluación</Alert>
    }

    const {
      productor,
      scoring,
      validaciones,
      patrimonio,
      capacidadPago,
      credito,
      alertas,
    } = evaluacion
    const recomendacion = getRecomendacion(scoring?.categoriaRiesgo)

    return (
      <>
        {/* HEADER - RECOMENDACIÓN */}
        <Card className={`border-${recomendacion.color} mb-4`}>
          <CardBody className={`bg-${recomendacion.color} bg-opacity-10`}>
            <Row className="align-items-center">
              <Col md={8}>
                <div className="d-flex align-items-center">
                  <div
                    className={`rounded-circle bg-${recomendacion.color} text-white d-flex align-items-center justify-content-center me-3`}
                    style={{ width: 60, height: 60 }}
                  >
                    <i
                      className={`mdi ${recomendacion.icon}`}
                      style={{ fontSize: 30 }}
                    />
                  </div>
                  <div>
                    <h3 className="mb-1">{recomendacion.texto}</h3>
                    <p className="mb-0 text-muted">
                      Score:
                      <Badge
                        color={getScoreColor(scoring?.categoriaRiesgo)}
                        className="ms-2"
                        style={{ fontSize: 16 }}
                      >
                        {scoring?.categoriaRiesgo || "N/D"}
                      </Badge>
                      <span className="ms-3">
                        Puntaje: {scoring?.scoreTotal || 0}/100
                      </span>
                    </p>
                  </div>
                </div>
              </Col>
              <Col md={4} className="text-end">
                <small className="text-muted d-block">
                  Evaluación #{idEvaluacion}
                </small>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Row>
          {/* COLUMNA IZQUIERDA */}
          <Col lg={8}>
            {/* Datos del Productor */}
            <Card className="mb-3">
              <CardHeader className="py-2">
                <h6 className="mb-0">
                  <i className="mdi mdi-account me-2" />
                  Datos del Productor
                </h6>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md={6}>
                    <Table size="sm" borderless className="mb-0">
                      <tbody>
                        <tr>
                          <td className="text-muted">Razón Social</td>
                          <td>
                            <strong>{productor?.razonSocial}</strong>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">CUIT</td>
                          <td>{productor?.cuit}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Email</td>
                          <td>{productor?.email || "-"}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                  <Col md={6}>
                    <div className="mb-2">
                      <small className="text-muted">Validación AFIP:</small>
                      {validaciones?.afip?.validado ? (
                        <Badge color="success" className="ms-2">
                          <i className="mdi mdi-check me-1" />
                          Validado
                        </Badge>
                      ) : (
                        <Badge color="warning" className="ms-2">
                          <i className="mdi mdi-alert me-1" />
                          No validado
                        </Badge>
                      )}
                    </div>
                    <div>
                      <small className="text-muted">Stock:</small>
                      <Badge color="warning" className="ms-2">
                        <i className="mdi mdi-file-document me-1" />
                        Declaración Jurada
                      </Badge>
                      <br />
                      <small className="text-muted">
                        Sin validación SENASA
                      </small>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Patrimonio */}
            <Card className="mb-3">
              <CardHeader className="py-2">
                <h6 className="mb-0">
                  <i className="mdi mdi-bank me-2" />
                  Patrimonio Estimado
                </h6>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md={4} className="text-center border-end">
                    <small className="text-muted d-block">
                      Superficie Total
                    </small>
                    <h4 className="mb-0">
                      {patrimonio?.superficieTotal?.toLocaleString() || 0} ha
                    </h4>
                  </Col>
                  <Col md={4} className="text-center border-end">
                    <small className="text-muted d-block">Stock Ganadero</small>
                    <h4 className="mb-0">
                      {patrimonio?.totalCabezas?.toLocaleString() || 0} cab
                    </h4>
                  </Col>
                  <Col md={4} className="text-center">
                    <small className="text-muted d-block">
                      Patrimonio Total
                    </small>
                    <h4 className="mb-0 text-success">
                      {formatMoney(patrimonio?.patrimonioTotal)}
                    </h4>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Alertas */}
            {alertas && alertas.length > 0 && (
              <Card className="mb-3 border-warning">
                <CardHeader className="py-2 bg-warning bg-opacity-10">
                  <h6 className="mb-0">
                    <i className="mdi mdi-alert me-2" />
                    Alertas ({alertas.length})
                  </h6>
                </CardHeader>
                <CardBody className="p-0">
                  <Table size="sm" className="mb-0">
                    <tbody>
                      {alertas.map((a, idx) => (
                        <tr key={idx}>
                          <td width="80">
                            <Badge
                              color={
                                a.severidad === "ALTA"
                                  ? "danger"
                                  : a.severidad === "MEDIA"
                                    ? "warning"
                                    : "info"
                              }
                            >
                              {a.severidad}
                            </Badge>
                          </td>
                          <td>
                            <strong>{a.tipo}:</strong> {a.mensaje}
                            {a.recomendacion && (
                              <small className="d-block text-muted">
                                {a.recomendacion}
                              </small>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            )}

            {/* Análisis Avanzado */}
            <Card className="mb-3">
              <CardHeader
                className="py-2 bg-light"
                style={{ cursor: "pointer" }}
                onClick={() => setMostrarAnalisis(!mostrarAnalisis)}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">
                    <i className="mdi mdi-satellite-variant me-2" />
                    Análisis Avanzado
                  </h6>
                  <i
                    className={`mdi mdi-chevron-${mostrarAnalisis ? "up" : "down"}`}
                  />
                </div>
              </CardHeader>
              <Collapse isOpen={mostrarAnalisis}>
                <CardBody>
                  <Link
                    to={`/agro/productores/${productor?.id}`}
                    className="btn btn-outline-primary btn-sm me-2 mb-2"
                  >
                    <i className="mdi mdi-satellite-variant me-1" />
                    Ver Imágenes Satelitales
                  </Link>
                  <Link
                    to={`/agro/productores/${productor?.id}`}
                    className="btn btn-outline-success btn-sm me-2 mb-2"
                  >
                    <i className="mdi mdi-leaf me-1" />
                    Ver Índice NDVI
                  </Link>
                  <Link
                    to={`/agro/productores/${productor?.id}`}
                    className="btn btn-outline-info btn-sm mb-2"
                  >
                    <i className="mdi mdi-terrain me-1" />
                    Ver Datos de Suelo
                  </Link>
                </CardBody>
              </Collapse>
            </Card>
          </Col>

          {/* COLUMNA DERECHA */}
          <Col lg={4}>
            {/* Crédito Solicitado */}
            <Card className="mb-3 border-primary">
              <CardHeader className="py-2 bg-primary text-white">
                <h6 className="mb-0">
                  <i className="mdi mdi-cash me-2" />
                  Crédito Solicitado
                </h6>
              </CardHeader>
              <CardBody>
                <div className="text-center mb-3">
                  <small className="text-muted">Monto</small>
                  <h2 className="mb-0">
                    {formatMoney(credito?.montoSolicitado)}
                  </h2>
                </div>
                <Table size="sm" borderless>
                  <tbody>
                    <tr>
                      <td className="text-muted">Destino</td>
                      <td className="text-end">
                        {credito?.destino?.replace(/_/g, " ")}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Plazo</td>
                      <td className="text-end">{credito?.plazoMeses} meses</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Cuota est.</td>
                      <td className="text-end">
                        <strong>
                          {formatMoney(capacidadPago?.cuotaMensualEstimada)}
                        </strong>
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Garantía</td>
                      <td className="text-end">
                        {credito?.garantia || "Sin garantía"}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </CardBody>
            </Card>

            {/* Capacidad de Pago */}
            <Card className="mb-3">
              <CardHeader className="py-2">
                <h6 className="mb-0">
                  <i className="mdi mdi-calculator me-2" />
                  Capacidad de Pago
                </h6>
              </CardHeader>
              <CardBody>
                <Table size="sm" borderless>
                  <tbody>
                    <tr>
                      <td className="text-muted">Ingreso anual</td>
                      <td className="text-end">
                        {formatMoney(capacidadPago?.ingresoAnualEstimado)}
                      </td>
                    </tr>
                  </tbody>
                </Table>
                <div className="mt-3">
                  <div className="d-flex justify-content-between">
                    <small className="text-muted">Ratio cuota/ingreso</small>
                    <small
                      className={
                        capacidadPago?.ratioCuotaIngreso <= 30
                          ? "text-success"
                          : capacidadPago?.ratioCuotaIngreso <= 40
                            ? "text-warning"
                            : "text-danger"
                      }
                    >
                      {capacidadPago?.ratioCuotaIngreso?.toFixed(1) || 0}%
                    </small>
                  </div>
                  <Progress
                    value={capacidadPago?.ratioCuotaIngreso || 0}
                    max={60}
                    color={
                      capacidadPago?.ratioCuotaIngreso <= 30
                        ? "success"
                        : capacidadPago?.ratioCuotaIngreso <= 40
                          ? "warning"
                          : "danger"
                    }
                    className="mt-1"
                  />
                  <small className="text-muted">Recomendado: hasta 30%</small>
                </div>
              </CardBody>
            </Card>

            {/* Score */}
            <Card className="mb-3">
              <CardHeader className="py-2">
                <h6 className="mb-0">
                  <i className="mdi mdi-chart-arc me-2" />
                  Score
                </h6>
              </CardHeader>
              <CardBody>
                <div className="text-center mb-3">
                  <div
                    className={`rounded-circle bg-${getScoreColor(scoring?.categoriaRiesgo)} text-white d-inline-flex align-items-center justify-content-center`}
                    style={{ width: 80, height: 80, fontSize: 32 }}
                  >
                    {scoring?.categoriaRiesgo || "?"}
                  </div>
                  <div className="mt-2">
                    <strong>{scoring?.scoreTotal || 0}</strong> / 100
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Botones de Decisión */}
            <Card className="border-dark">
              <CardBody>
                <h6 className="text-center mb-3">Decisión del Oficial</h6>
                <div className="d-grid gap-2">
                  <Button
                    color="success"
                    size="lg"
                    onClick={handleAprobar}
                    disabled={procesando || evaluacion.estado !== "PENDIENTE"}
                  >
                    {procesando ? (
                      <Spinner size="sm" />
                    ) : (
                      <i className="mdi mdi-check me-2" />
                    )}
                    APROBAR
                  </Button>
                  <Button
                    color="danger"
                    size="lg"
                    outline
                    onClick={handleRechazar}
                    disabled={procesando || evaluacion.estado !== "PENDIENTE"}
                  >
                    <i className="mdi mdi-close me-2" />
                    RECHAZAR
                  </Button>
                </div>
                {evaluacion.estado !== "PENDIENTE" && (
                  <Alert color="info" className="mt-3 mb-0 text-center">
                    <small>
                      Esta evaluación ya fue {evaluacion.estado?.toLowerCase()}
                    </small>
                  </Alert>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </>
    )
  }

  // =====================================================
  // BREADCRUMB
  // =====================================================
  const breadcrumbItems = [
    { title: "Agropecuario", link: "/agro" },
    { title: "Evaluaciones", link: "/agro/evaluaciones" },
    { title: `Evaluación #${idEvaluacion}`, link: "#" },
  ]

  return (
    <Layout
      components={[{ component: <MainContent /> }]}
      title={`Resumen Ejecutivo - Evaluación #${idEvaluacion}`}
      breadcrumbItems={breadcrumbItems}
      permissionBy={permissionBy}
    />
  )
}

export default ResumenEjecutivo
